import * as React from 'react';
import AttentionBox from 'sos-common/js/components/AttentionBox/Attentionbox';
import { Expander } from 'sos-common/js/components/Expander/Expander';
import { Fileupload } from 'sos-common/js/components/Fileupload/Fileupload';
import { CheckBox } from 'sos-common/js/components/FormControls/CheckBox';
import { setInnerHtml, formatString } from 'sos-common/js/utils/helpers';
import { SectionClass, SectionState, SectionProps, FieldName, FormType } from '../PublicForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { DocumentModel } from '../DocumentModel/document.model.types';
import { FormFieldFile, FormField } from 'sos-common/js/commonTypes/formField.types';
import { FileExtendedDetails } from 'sos-common/js/commonTypes/fileupload.types';
import _ from 'lodash';
import { validateFiles, FileValidation } from '../../../../js/services/fileupload.clientonly.validation.service';

export interface IDokumentSectionProps extends SectionProps {
  documents: DocumentModel[];
  maxTotalSizeInBytes: number;
  maxIndividualFileSizeInBytes: number;
  maxFileuploadErrorMessage?: string;
  hideOriginalDocumentsCheckbox?: boolean;
  acceptedFileTypes?: string[];
  specialistClicked?: boolean;
  fileInputChanged: (files: FormFieldFile) => void;
}

export interface IDokumentSectionState extends SectionState {
  laddatOriginalDokument: boolean;
  totalAttachedDocuments: Map<string, number>;
}

const BYTES_IN_MEGABYTE: number = 1_000_000;
const StringFormat = (str: string, ...args: string[]) => str.replace(/{(\d+)}/g, (match, index) => args[index] || '');
const DEFAULT_MIMETYPES: string = '.pdf,.txt,.docx,.doc,.dot,.jpeg,.jpg,.jpe,.jif,.jfif,.jfi,.png';
export default class DokumentSection extends SectionClass<IDokumentSectionProps, IDokumentSectionState> {
  private _mandatoryString: string;
  private _acceptFiles: string;
  static defaultProps = {
    maxFileuploadErrorMessage: 'Du kan inte ladda upp fler än {0} filer',
  };
  constructor(props: IDokumentSectionProps) {
    super(props);
    this._mandatoryString = ' ' + props.Translate('mandatory');
    this._acceptFiles =
      !Array.isArray(props.acceptedFileTypes) || !props.acceptedFileTypes.length
        ? DEFAULT_MIMETYPES
        : props.acceptedFileTypes.map((x) => '.' + x).join(',');
    this.state = {
      errors: this.getInitialErrorMap(this.getRequiredFields(props.documents)),
      laddatOriginalDokument: false,
      totalAttachedDocuments: new Map<string, number>(),
    };
  }

  getRequiredFields = (documents: DocumentModel[]) => {
    const RequiredFieldList = [];
    documents?.forEach((doc) => {
      if (doc.isRequired) RequiredFieldList.push(doc.fieldName);
    });
    if (!this.props.hideOriginalDocumentsCheckbox) {
      RequiredFieldList.push(FieldName.LaddatOriginalDokument);
    }
    return RequiredFieldList;
  };

  //When choosing specialist some new documents are added. Theese need som special logic when added. Otherwise the errors will be removed
  getNewlyAddedSpecialistDocuments = (prevPropsDocuments: DocumentModel[], documents: DocumentModel[]) => {
    const { specialistClicked } = this.props;
    let newSpecialistDocuments: DocumentModel[];

    if (specialistClicked && prevPropsDocuments.length < documents.length) {
      newSpecialistDocuments = _.differenceBy(documents, prevPropsDocuments, 'fieldName');
    }

    return newSpecialistDocuments;
  };

  componentDidUpdate(prevProps: IDokumentSectionProps) {
    const { documents, maxTotalSizeInBytes, hideOriginalDocumentsCheckbox } = this.props;
    const { laddatOriginalDokument, totalAttachedDocuments } = this.state;

    if (!_.isEqual(prevProps.documents, documents)) {
      // Get a fresh error map based on initial setup
      const errors = this.getInitialErrorMap(this.getRequiredFields(documents));
      //This means that more documents is added, which happens when you choose specialistyrken.
      //We cant compare with PrevError like the other since theese dont exist in the state errors before and the error message will therebore be removed.
      const newSpecialistDocuments = this.getNewlyAddedSpecialistDocuments(prevProps.documents, documents);
      let totalBytesForEverything = 0;
      // Check if any values remain from last update
      documents.forEach((doc) => {
        const prevError = this.state.errors.get(doc.fieldName);
        if (prevError === undefined) {
          //the document is a newly added, and should therefore not be removed since it does not exist in the state and the initialErrorMap should apply.
          if (!_.includes(newSpecialistDocuments, doc)) {
            // Value exists && no prevError == valid field (remove error)
            errors.delete(doc.fieldName);
          }
        } else {
          // Value exists && got prevError == invalid field (retain error)
          errors.set(doc.fieldName, this.state.errors.get(doc.fieldName));
        }

        //Total size calculation
        const currentFileuploadComponentBytes = totalAttachedDocuments.get(doc.fieldName);
        if (currentFileuploadComponentBytes !== undefined) {
          totalBytesForEverything += currentFileuploadComponentBytes;
        }
      });

      //Set total size error if exists
      if (totalBytesForEverything > maxTotalSizeInBytes) {
        errors.set('TOTAL_MAX_FILE_SIZE', {
          errorMessage: this.props.Translate('error_total_max_file_size'),
          showMessage: true,
        });
      } else {
        errors.delete('TOTAL_MAX_FILE_SIZE');
      }

      // Retain the LaddatOriginalDokument value from previous state
      if (!laddatOriginalDokument && !hideOriginalDocumentsCheckbox) {
        errors.set(FieldName.LaddatOriginalDokument, this.state.errors.get(FieldName.LaddatOriginalDokument));
      } else {
        errors.delete(FieldName.LaddatOriginalDokument);
      }

      this.setState({ errors });
    }
  }

  removeFile = (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => {
    field.value = field.value.filter((x) => x.FileId !== file.FileId);

    this.validateFiles(field);
  };

  validateFiles = (files: FormFieldFile) => {
    const {
      maxIndividualFileSizeInBytes,
      maxTotalSizeInBytes,
      Translate,
      maxFileuploadErrorMessage,
      acceptedFileTypes,
    } = this.props;
    const errors = new Map(this.state.errors);
    const isRequired = this.props.documents.find((x) => x.fieldName === files.fieldName).isRequired;
    const fileValidation = validateFiles(files, 10, maxIndividualFileSizeInBytes, isRequired);
    switch (fileValidation) {
      case FileValidation.amountOfFilesExceeded:
        errors.set(files.fieldName, {
          errorMessage: StringFormat(maxFileuploadErrorMessage, '10'),
          showMessage: true,
        });
        break;
      case FileValidation.individualFileSizeExceeded:
        errors.set(files.fieldName, {
          errorMessage:
            Translate('error_maxstorlek') + Math.round(maxIndividualFileSizeInBytes / BYTES_IN_MEGABYTE) + ' MB',
          showMessage: true,
        });
        break;
      case FileValidation.isRequiredWithZeroFiles:
        errors.set(files.fieldName, {
          errorMessage: Translate('error_mandatory'),
          showMessage: true,
        });
        break;
      case FileValidation.valid:
        errors.delete(files.fieldName);
        break;
    }

    const totalAttachedDocuments = new Map(this.state.totalAttachedDocuments);
    let totalSizeForThisFileUploadComponent = 0;
    files.value.forEach((fileExtendedDetail) => {
      totalSizeForThisFileUploadComponent += fileExtendedDetail.file.size;
    });
    totalAttachedDocuments.set(files.fieldName, totalSizeForThisFileUploadComponent);

    this.props.fileInputChanged(files);

    this.setState({ totalAttachedDocuments, errors }, () => {
      this.props.onChangeFieldValue();
    });
  };

  onCheckboxChange = (field: FormField<boolean>) => {
    const errors = new Map(this.state.errors);
    const laddatOriginalDokument = field.value;
    if (!laddatOriginalDokument) {
      errors.set(FieldName.LaddatOriginalDokument, { showMessage: false, errorMessage: null });
    } else {
      errors.delete(FieldName.LaddatOriginalDokument);
    }
    this.setState({ laddatOriginalDokument, errors }, () => {
      this.onChangeField({ ...field, value: field.value || null });
    });
  };

  public render() {
    const {
      Translate,
      isInEditMode,
      openWebAnsokanEpiTexts,
      documents,
      formType,
      skyddadYrkestitelOpenFormEpiTexts,
      hideOriginalDocumentsCheckbox,
      acceptedFileTypes,
    } = this.props;
    const { laddatOriginalDokument } = this.state;
    return (
      <section aria-labelledby="dokument-heading">
        <EpiserverProperty propertyName="DokumentHeading" isInEditMode={isInEditMode}>
          <h2 id="dokument-heading">
            {formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.DokumentHeading
              : openWebAnsokanEpiTexts.DokumentHeading}
          </h2>
        </EpiserverProperty>

        <EpiserverProperty propertyName="DokumentPreamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">
            {formType == FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.DokumentPreamble
              : openWebAnsokanEpiTexts.DokumentPreamble}
          </p>
        </EpiserverProperty>

        {(isInEditMode ||
          (formType == FormType.SkyddadYrkestitel && skyddadYrkestitelOpenFormEpiTexts?.DokumentInforuta)) && (
          <AttentionBox
            cssClass="start-new-ansokan__warning-text attentionbox--brown m-b-2"
            icon="Infocirkel_normal20px20px"
            bodytext={skyddadYrkestitelOpenFormEpiTexts.DokumentInforuta}
          />
        )}
        {(isInEditMode || (formType === FormType.Legitimation && openWebAnsokanEpiTexts?.DokumentInforuta)) && (
          <AttentionBox
            cssClass="start-new-ansokan__warning-text attentionbox--brown m-b-2"
            icon="Infocirkel_normal20px20px"
            bodytext={openWebAnsokanEpiTexts.DokumentInforuta}
          />
        )}
        <EpiserverProperty propertyName="DokumentBody" usingDangerouslySetInnerHTML={true} isInEditMode={isInEditMode}>
          <div
            className="m-t-1"
            dangerouslySetInnerHTML={setInnerHtml(
              formType == FormType.SkyddadYrkestitel
                ? skyddadYrkestitelOpenFormEpiTexts.DokumentBody
                : openWebAnsokanEpiTexts.DokumentBody,
            )}
          />
        </EpiserverProperty>

        {(isInEditMode ||
          (formType == FormType.SkyddadYrkestitel && skyddadYrkestitelOpenFormEpiTexts?.DokumentBoxHeading)) && (
          <Expander heading={skyddadYrkestitelOpenFormEpiTexts.DokumentBoxHeading} isExpanded={false}>
            <EpiserverProperty
              propertyName="DokumentBoxBody"
              usingDangerouslySetInnerHTML={true}
              isInEditMode={isInEditMode}>
              <div dangerouslySetInnerHTML={setInnerHtml(skyddadYrkestitelOpenFormEpiTexts.DokumentBoxBody)} />
            </EpiserverProperty>
          </Expander>
        )}
        {(isInEditMode || (formType == FormType.Legitimation && openWebAnsokanEpiTexts?.DokumentBoxHeading)) && (
          <Expander heading={openWebAnsokanEpiTexts.DokumentBoxHeading} isExpanded={false}>
            <EpiserverProperty
              propertyName="DokumentBoxBody"
              usingDangerouslySetInnerHTML={true}
              isInEditMode={isInEditMode}>
              <div dangerouslySetInnerHTML={setInnerHtml(openWebAnsokanEpiTexts.DokumentBoxBody)} />
            </EpiserverProperty>
          </Expander>
        )}

        {documents?.map((document) => {
          return (
            <div className="m-t-2" key={document.fieldName}>
              <Fileupload
                heading={document.title || Translate(document.fieldName)}
                errorMessage={this.getErrorMessage(document.fieldName)}
                fieldName={document.fieldName}
                isRequired={document.isRequired}
                mandatory={document.isRequired}
                disabled={false}
                onAdd={this.validateFiles}
                onRemove={this.removeFile}
                clientOnly={true}
                clientOnlyLegitimation={true}
                value={document.files}
                max={10}
                maxFileSize={this.props.maxIndividualFileSizeInBytes}
                maxFileSizeErrorMessage={Translate('maxstorlek')}
                mimetype={this._acceptFiles}
                infoBubble={{ text: document.infobubble, isInEditMode, propertyName: 'bubble_' + document.fieldName }}
                upload={Translate('laddaupp')}
                nofilesText={Translate('ingafiler')}
                submitButtonIsClicked={this.props.submitButtonIsClicked}
                removeText={Translate('fileupload_remove') || undefined}
                removeFileText={Translate('fileupload_removefile') || undefined}
                removeFilePopupBody={Translate('fileupload_removefilepopupbody') || undefined}
                abortText={Translate('fileupload_cancel') || undefined}
                closeText={Translate('fileupload_close') || undefined}
                okText={Translate('fileupload_ok') || undefined}
                mandatoryString={Translate('mandatory')}
                acceptedFileTypes={acceptedFileTypes}
                wrongFileTypeErrorMessage={StringFormat(
                  Translate('error_filetype'),
                  this._acceptFiles?.replaceAll(',', ', '),
                )}
              />
            </div>
          );
        })}

        {this.getErrorMessage('TOTAL_MAX_FILE_SIZE') && (
          <div role="alert">
            <AttentionBox
              cssClass="attentionbox--red"
              icon="Summering_inte_OK25px25px"
              bodytext={this.getErrorMessage('TOTAL_MAX_FILE_SIZE')}
            />
          </div>
        )}

        {!hideOriginalDocumentsCheckbox && (
          <CheckBox
            fieldName={FieldName.LaddatOriginalDokument}
            label={Translate(FieldName.LaddatOriginalDokument) + this._mandatoryString}
            onChange={this.onCheckboxChange}
            stringValue={Translate('ja')}
            isRequired={true}
            errorMessage={this.getErrorMessage(FieldName.LaddatOriginalDokument)}
          />
        )}

        {!laddatOriginalDokument && !hideOriginalDocumentsCheckbox && (
          <input type="hidden" name={FieldName.LaddatOriginalDokument} value={Translate('nej')} />
        )}
        <hr className="formdivider" />
      </section>
    );
  }
}
